/**
 * Mapowanie `targetModuleCode` → URL strony rekordu (F17 T17.1.6 v3).
 *
 * Backend zwraca instancje z polami `targetModuleCode` + `targetRecordId`
 * (np. `crm_core.contractors` + UUID kontrahenta). Frontend listingu klika
 * wiersz i navigate'uje do strony rekordu — ale każdy moduł ma własny URL.
 *
 * **Slownik mieszka tu (frontend), nie w backend** — backend pozostaje
 * agnostyczny URL-i SPA. Każdy nowy moduł integrowany z procesami = jedna
 * linijka w `STATIC_ROUTES` lub fallback dla `custom.<slug>`. Patrz ADR-0037
 * §"Powiązane decyzje" — opcja A.
 *
 * Konwencja kodów modułu z `apps/api/lib/processes/engine.ts`
 * `TARGET_MODULE_FETCH_MAP`:
 *   - `crm_core.<entity>` — moduły hardcoded w SaaS (kontrahenci, faktury…).
 *   - `custom.<slug>` — moduły zdefiniowane przez admin tenanta w F12 builderze
 *     (slug pochodzi z `custom_modules.url_slug` w tenant DB).
 */

import { HANDEL_TARGET_MODULE_CODE } from '@saas/shared';

/**
 * Kod modułu kontrahentów w obiegu akceptacji — cel procesu i część klucza zapytań o sprawy rekordu
 * (`recordInstancesKey`). Jedno źródło dla karty kontrahenta, jego zakładki „Dostępy firm" i testów.
 */
export const CONTRACTOR_MODULE_CODE = 'crm_core.contractors';

/**
 * Kod modułu dokumentów w obiegu akceptacji — jeden dla wszystkich klas dokumentów (faktury,
 * umowy, pisma, dokumenty specjalne itd.). Baner procesu na karcie dokumentu i unieważnianie
 * listingu spraw po zapisie MUSZĄ używać tego samego kodu, inaczej trafiałyby w różne klucze
 * zapytań.
 */
export const DOCUMENTS_MODULE_CODE = 'crm_core.documents';

/**
 * Prefiks kodu modułu WŁASNEGO (kartoteki z kreatora) w obiegu akceptacji: `custom.<slug>`. Ten sam
 * zapis jest kodem celu procesu i kluczem grupy sekcji kokpitu decyzji („Grupuj według: Kartoteka"),
 * więc trasa rekordu, etykieta modułu i zestaw sekcji rozpoznają kartotekę własną po jednym prefiksie.
 */
export const CUSTOM_MODULE_CODE_PREFIX = 'custom.';

// dormant: wpisy doc_* poniżej są nieosiągalne dopóki SUPPORTED_PROCESS_TARGET_MODULES
// nie zostanie rozszerzone o klasy dokumentów — scaffold, nie martwy kod do usunięcia.
const STATIC_ROUTES: Record<string, (id: string) => string> = {
  [CONTRACTOR_MODULE_CODE]: (id) => `/contractors/${id}`,
  'doc_invoices.purchase_invoices': (id) => `/purchase-invoices/${id}`,
  'doc_invoices.sale_invoices': (id) => `/sale-invoices/${id}`,
  // Document class modules (F20 T20.18) — detail routes active from Etap 3
  'doc_contracts.contracts': (id) => `/contracts/${id}`,
  'doc_letters.letters': (id) => `/letters/${id}`,
  'doc_commercial.commercial': (id) => `/commercial-docs/${id}`,
  'doc_internal.internal': (id) => `/internal-docs/${id}`,
  'doc_specjalne.specjalne': (id) => `/special-docs/${id}`,
};

/**
 * Zwraca pełen URL strony rekordu LUB `null` gdy moduł nieobsługiwany
 * (np. `custom.<slug>` przed T17.1.9 lub nieznany kod). Frontend listingu
 * pokazuje wtedy disabled link / brak klikalności.
 */
export function resolveTargetUrl(moduleCode: string, recordId: string): string | null {
  const builder = STATIC_ROUTES[moduleCode];
  if (builder) return builder(recordId);
  if (moduleCode.startsWith(CUSTOM_MODULE_CODE_PREFIX)) {
    const slug = moduleCode.slice(CUSTOM_MODULE_CODE_PREFIX.length);
    if (slug.length === 0) return null;
    return `/modules/${slug}/${recordId}`;
  }
  return null;
}

/**
 * Czy klik w wiersz celu ma prowadzić do kartoteki (sync — do stylowania wiersza PRZED klikiem).
 * Dla dokumentu ZAWSZE `true`: dokumenty dzielą jeden kod celu `crm_core.documents` dla 7 klas,
 * a właściwą trasę wybieramy po klasie dopiero NA kliknięciu (`resolveTargetHref` w
 * `use-navigate-to-target.ts` dociąga klasę po id). Dla pozostałych typów = czy `resolveTargetUrl`
 * daje trasę synchronicznie (kontrahent/moduł własny tak; nieznany kod / pusty slug → nie).
 */
export function isTargetNavigable(moduleCode: string, recordId: string): boolean {
  if (moduleCode === DOCUMENTS_MODULE_CODE) return true;
  // Dokument z Symfonii Handel — jak dokument: trasę da się zbudować dopiero po dociągnięciu
  // rezerwacji identyfikatora (rodzaj kartoteki + numer w Symfonii), czyli na kliknięciu.
  if (moduleCode === HANDEL_TARGET_MODULE_CODE) return true;
  return resolveTargetUrl(moduleCode, recordId) !== null;
}

/**
 * Klucz i18n dla labela modułu (`tenant.processes.moduleLabel.<key>`).
 * Konwersja kropki/myślnika na underscore żeby pasowało do JSON i18n key.
 * Zwraca `tenant.processes.moduleLabel.custom` dla custom modułów oraz
 * `tenant.processes.moduleLabel.unknown` dla nieznanych kodów.
 *
 * Celowo odsprzężone od STATIC_ROUTES — `crm_core.documents` ma label
 * (Dokumenty) ale NIE ma trasy (backend używa jednego kodu dla 7 klas,
 * frontend nie ma strony /documents/:id). Dodaj tu, a nie w STATIC_ROUTES.
 */
const KNOWN_MODULE_LABELS = new Set<string>([
  CONTRACTOR_MODULE_CODE,
  'doc_invoices.purchase_invoices',
  'doc_invoices.sale_invoices',
  DOCUMENTS_MODULE_CODE,
  HANDEL_TARGET_MODULE_CODE,
]);

export function moduleLabelI18nKey(moduleCode: string): string {
  if (KNOWN_MODULE_LABELS.has(moduleCode)) {
    return `tenant.processes.moduleLabel.${moduleCode.replace(/\./g, '_')}`;
  }
  if (moduleCode.startsWith(CUSTOM_MODULE_CODE_PREFIX)) {
    return 'tenant.processes.moduleLabel.custom';
  }
  return 'tenant.processes.moduleLabel.unknown';
}
